/**
 * @file components/layout/AppShell.tsx
 * @description 後台應用主殼層 — 根據用戶 workbenchType 分發到對應的角色專用佈局。
 *
 * 工作台類型 → 佈局映射：
 *   - admin      → AdminLayout（深色側邊欄 240px，完整系統設置）
 *   - manager    → AdminLayout（深色側邊欄 240px，業務管理功能）
 *   - decider    → DeciderLayout（白色側邊欄 230px，Sky→Teal 漸變）
 *   - auditor    → AuditorLayout（白色側邊欄 220px，Indigo→Violet 漸變，頂欄含搜尋）
 *   - accountant（序52）→ AdminLayout（深色殼，聚焦報價／繳費管理的派案專區）
 *
 * 分發優先序（Task 9）：
 *   1. user.workbenchType（後端 DB 動態 RBAC，最準確）
 *   2. BUILTIN_ROLE_WORKBENCH[user.primaryRole]（內置角色映射，向後相容舊 persist 資料）
 *   3. 'auditor'（終極回落，確保頁面可渲染）
 *
 * 舊 localStorage persist（無 workbenchType 欄位）在用戶下次 /me 重新整理後自癒。
 * AuthProvider 在應用啟動時打一次 /api/auth/me，因此刷新後即攜帶最新 workbenchType。
 *
 * 各佈局均基於 BaseLayout 殼（毛玻璃 Header + ⌘K 命令面板 + 通知抽屜 + collapsed Sider）。
 * OfflineBanner 由 BaseLayout 內容區前插入（透過 siderTopExtra 插槽）。
 *
 * 使用範例（在 _app/route.tsx 中）：
 * ```tsx
 * <AppShell>
 *   <Outlet />
 * </AppShell>
 * ```
 */

import { useEffect, type ReactNode } from 'react';
import { BUILTIN_ROLE_WORKBENCH, type WorkbenchType } from '@zhcb/shared';
import AdminLayout from '@/components/layout/AdminLayout';
import AuditorLayout from '@/components/layout/AuditorLayout';
import DeciderLayout from '@/components/layout/CounselorLayout';
import { OfflineBanner } from '@/components/data/OfflineBanner';
import { useRouter } from '@/lib/navigation';
import { useAuthStore } from '@/lib/auth-context';
import { setOfflineFocusIntent, useOfflineFocusMode } from '@/hooks/useOfflineFocusMode';

/**
 * 從用戶物件推導工作台類型。
 *
 * 回落語義：
 * - 有 workbenchType → 直接使用（DB 動態 RBAC 計算值，最準確）
 * - 無 workbenchType 但有 primaryRole → 查 BUILTIN_ROLE_WORKBENCH 映射（兼容舊 persist 資料）
 * - 均無或無法映射 → 'auditor'（安全回落，確保佈局可渲染）
 */
function resolveWorkbenchType(
  workbenchType: string | undefined,
  primaryRole: string | undefined,
): WorkbenchType {
  if (workbenchType) return workbenchType as WorkbenchType;
  const mapped = primaryRole
    ? BUILTIN_ROLE_WORKBENCH[primaryRole as keyof typeof BUILTIN_ROLE_WORKBENCH]
    : undefined;
  return mapped ?? 'auditor';
}

/**
 * 應用主殼層組件。
 * 根據 user.workbenchType（回落 primaryRole → 'auditor'）分發到對應的角色佈局。
 * 各角色佈局均已內嵌 BaseLayout（含 Sider、Header、CommandPalette、NotificationDrawer）。
 */
export function AppShell({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { isAuditorOffline, canStayInOfflineWorkspace, focusMode } = useOfflineFocusMode();
  /* 讀取完整 user 物件，供 workbenchType 推導與菜單過濾使用 */
  const user = useAuthStore((s) => s.user);

  /* 推導當前工作台類型（動態 RBAC 優先，回落內置映射，最終落 auditor） */
  const workbenchType = resolveWorkbenchType(user?.workbenchType, user?.primaryRole);

  useEffect(() => {
    if (!isAuditorOffline) return;
    if (canStayInOfflineWorkspace) {
      setOfflineFocusIntent(true);
    } else {
      router.replace('/me/offline');
    }
  }, [canStayInOfflineWorkspace, isAuditorOffline, router]);

  /* 離線專注模式只保留業務工作區，不再插入全站橫幅。 */
  const content = focusMode ? (
    children
  ) : (
    <>
      <OfflineBanner />
      {children}
    </>
  );

  /* 根據工作台類型選擇對應佈局（稽核工作台同時傳入 focusMode） */
  if (workbenchType === 'auditor') {
    return <AuditorLayout focusMode={focusMode}>{content}</AuditorLayout>;
  }

  if (workbenchType === 'decider') {
    return <DeciderLayout>{content}</DeciderLayout>;
  }

  /* 會計（序52）：複用 admin 深色殼，菜單聚焦報價／繳費管理（派案專區） */
  if (workbenchType === 'accountant') {
    return <AdminLayout workbenchType="accountant">{content}</AdminLayout>;
  }

  /* admin / manager 共用深色側邊欄佈局，通過 workbenchType prop 切換菜單 */
  return (
    <AdminLayout workbenchType={workbenchType === 'manager' ? 'manager' : 'admin'}>
      {content}
    </AdminLayout>
  );
}
